/**
 * RN-03 do PRD 32 — a definição única de "preenchido" da folha de monitoramento,
 * válida tanto em granularidade de célula quanto de linha.
 *
 * Uma linha é **vazia** quando seu único conteúdo são células de data com valor igual
 * à `referenceDate` do container; torna-se **em uso** ao receber valor em célula que não
 * é de data, checkbox marcado, ou data preenchida e diferente da `referenceDate`. A partir
 * de "em uso", todos os campos são obrigatórios — a linha fica **completa** ou **parcial**.
 *
 * O estado é derivado do conteúdo atual, nunca persistido: apagar o que colocou a linha
 * em uso a devolve a vazia sozinho.
 */

import { toCanonicalDateOnly } from '@/utils/date'

export type RowStatus = 'empty' | 'partial' | 'complete'

/** Subconjunto de `SheetColumn` de que a classificação depende. */
export type ClassifiableColumn = {
  key: string
  type: string
}

export type SheetRow = Record<string, unknown>

const DATE_COLUMN_TYPE = 'date'

/**
 * RN-03 em granularidade de célula: checkbox conta como preenchido apenas quando
 * marcado; qualquer outro tipo, quando tem valor não-vazio.
 */
export function isCellFilled(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  return String(value ?? '').trim() !== ''
}

/**
 * RN-03 — a célula conta como conteúdo transcrito pelo auditor (o que coloca a linha
 * "em uso"). Exceção exclusiva da coluna `date`: valor igual à `referenceDate` é a
 * sugestão da RF-01, não conteúdo, independentemente de como chegou à célula.
 */
export function isCellInUse(
  value: unknown,
  column: ClassifiableColumn,
  referenceDate: string
): boolean {
  if (!isCellFilled(value)) return false
  if (column.type === DATE_COLUMN_TYPE) {
    return toCanonicalDateOnly(value) !== toCanonicalDateOnly(referenceDate)
  }
  return true
}

/** RN-03 — classifica a linha pelo conteúdo atual das colunas do modelo de folha. */
export function classifyRow(
  row: SheetRow,
  columns: ClassifiableColumn[],
  referenceDate: string
): RowStatus {
  if (columns.length === 0) return 'empty'
  if (!columns.some((column) => isCellInUse(row[column.key], column, referenceDate))) return 'empty'
  return columns.every((column) => isCellFilled(row[column.key])) ? 'complete' : 'partial'
}

/**
 * RF-02 — colunas que faltam preencher numa linha em uso, na ordem do modelo. São as
 * células que recebem erro de validação enquanto a linha estiver parcial.
 */
export function missingCellKeys(row: SheetRow, columns: ClassifiableColumn[]): string[] {
  return columns.filter((column) => !isCellFilled(row[column.key])).map((column) => column.key)
}

/**
 * RF-01 — toda coluna do tipo `date` nasce preenchida com a `referenceDate` do container;
 * as demais nascem vazias. O valor é sugestão editável e não coloca a linha em uso.
 */
export function createEmptyRow(
  columns: ClassifiableColumn[],
  referenceDate: string
): Record<string, string | boolean> {
  return Object.fromEntries(
    columns.map((column) => {
      if (column.type === 'checkbox') return [column.key, false]
      if (column.type === DATE_COLUMN_TYPE) return [column.key, referenceDate]
      return [column.key, '']
    })
  )
}
